import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../api/client';

/** One Analysis Department Chain grid row (TestMethod + per-role user counts). */
export interface AdcRow {
  id: string;
  matrix: string | null;
  test: string | null;
  method: string | null;
  resultEntryCount: number;
  sdmsValidationCount: number;
  resultApprovalCount: number;
}

/** One assignable user (Oid == Employee Oid) — picker grid columns. */
export interface AdcUser {
  oid: string;
  fullName: string;
  displayName: string;   // employee code, e.g. "E182366"
  labUser: boolean;
}

/** Currently-assigned employee Oids for a TestMethod, per role. */
export interface AdcAssignments {
  resultEntry: string[];
  sdmsValidation: string[];
  resultApproval: string[];
}

export type AdcRole = 'resultEntry' | 'sdmsValidation' | 'resultApproval';

export const ADC_ROLE_LABEL: Record<AdcRole, string> = {
  resultEntry: 'Result Entry',
  sdmsValidation: 'SDMS Validation',
  resultApproval: 'Result Approval',
};

const ROWS_KEY = ['adc-rows'];
const USERS_KEY = ['adc-users'];

export function useAdcRows() {
  return useQuery({
    queryKey: ROWS_KEY,
    queryFn: async () => (await api.get<{ data: AdcRow[] }>('/api/v1/analysis-department-chain')).data.data,
  });
}

export function useAdcUsers() {
  return useQuery({
    queryKey: USERS_KEY,
    queryFn: async () => (await api.get<{ data: AdcUser[] }>('/api/v1/analysis-department-chain/users')).data.data,
    staleTime: 5 * 60_000,
  });
}

/** Assignments for one TestMethod — fetched on demand when a picker opens. */
export function useAdcAssignments(testMethodId: string | null) {
  return useQuery({
    queryKey: ['adc-assignments', testMethodId],
    enabled: !!testMethodId,
    queryFn: async () =>
      (await api.get<{ data: AdcAssignments }>(
        `/api/v1/analysis-department-chain/${testMethodId}/assignments`)).data.data,
  });
}

export function useSetAdcUsers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { testMethodId: string; role: AdcRole; employeeIds: string[] }) =>
      api.put(`/api/v1/analysis-department-chain/${v.testMethodId}/assignments`,
        { role: v.role, employeeIds: v.employeeIds }),
    onSuccess: (_d, v) => {
      void qc.invalidateQueries({ queryKey: ROWS_KEY });
      void qc.invalidateQueries({ queryKey: ['adc-assignments', v.testMethodId] });
    },
  });
}

/** Copy one source row's role assignments to several target rows ("Copy To All Cell"). */
export function useCopyAdcRole() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { sourceTestMethodId: string; role: AdcRole; targetTestMethodIds: string[] }) =>
      api.post('/api/v1/analysis-department-chain/copy-role',
        { sourceTestMethodId: v.sourceTestMethodId, role: v.role, targetTestMethodIds: v.targetTestMethodIds }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ROWS_KEY }); },
  });
}

/** Pull a human-readable message out of an axios error. */
export function errMessage(e: unknown, fallback = 'Something went wrong.'): string {
  const r = (e as { response?: { data?: { error?: { message?: string } } } })?.response;
  return r?.data?.error?.message ?? fallback;
}
